import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaHeartbeat, FaDatabase, FaServer, FaShieldAlt,
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaUserCircle, FaSignOutAlt, FaBell, FaSyncAlt, FaExclamationCircle, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const SystemHealth = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [frontendVersion, setFrontendVersion] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') { toast.error('Access denied'); navigate('/login'); return; }
    loadProfile(); loadHealth();
    fetch(`${window.location.origin}/version.json`).then(r => r.ok ? r.json() : null).then(d => setFrontendVersion(d)).catch(() => {});
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`); }
    } catch (e) {}
  };

  const loadHealth = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/health'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { setHealth(await res.json()); setLastRefresh(new Date()); }
      else toast.error('Failed to load health data');
    } catch (e) { toast.error('Health check failed'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { clearToken(); localStorage.removeItem('userRole'); localStorage.removeItem('token'); navigate('/login'); };
  const StatusIcon = ({ ok }) => ok ? <FaCheckCircle style={{ color: '#3fb950' }} /> : <FaTimesCircle style={{ color: '#f85149' }} />;

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header"><FaUserCircle className="cs-menu-avatar" /><div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div></div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="cs-layout">
        <CloudStaffSidebar active="system-health" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}><FaHeartbeat style={{ marginRight: '0.5rem', color: '#f85149' }} /> System Health</h1>
                <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>Real-time system monitoring</p>
              </div>
            </div>
            <button onClick={loadHealth} className="cs-primary-btn" disabled={loading}><FaSyncAlt /> Refresh</button>
          </div>

          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Checking system health...</p></div>
          ) : health ? (
            <>
              {/* Deployment Version */}
              <div className="cs-section" style={{ marginBottom: '2rem' }}>
                <h2 className="cs-section-title">Deployment Version</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
                  <div className="cs-stat-card">
                    <div className="cs-stat-info" style={{ width: '100%' }}>
                      <span className="cs-stat-value" style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>{health.deployment?.backend_version || '—'}</span>
                      <span className="cs-stat-label">Backend version</span>
                    </div>
                  </div>
                  <div className="cs-stat-card">
                    <div className="cs-stat-info" style={{ width: '100%' }}>
                      <span className="cs-stat-value" style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>{health.deployment?.git_commit_hash || '—'}</span>
                      <span className="cs-stat-label">Git commit</span>
                    </div>
                  </div>
                  <div className="cs-stat-card">
                    <div className="cs-stat-info" style={{ width: '100%' }}>
                      <span className="cs-stat-value" style={{ fontSize: '0.9rem' }}>
                        {(health.deployment?.last_deploy_timestamp || health.deployment?.server_started_at) ? new Date(health.deployment.last_deploy_timestamp || health.deployment.server_started_at).toLocaleString('en-GB') : '—'}
                      </span>
                      <span className="cs-stat-label">Last deploy</span>
                    </div>
                  </div>
                  <div className="cs-stat-card">
                    <div className="cs-stat-info" style={{ width: '100%' }}>
                      <span className="cs-stat-value" style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>{frontendVersion?.version || '—'}</span>
                      <span className="cs-stat-label">Frontend build</span>
                    </div>
                  </div>
                  <div className="cs-stat-card">
                    <div className="cs-stat-info" style={{ width: '100%' }}>
                      <span className="cs-stat-value" style={{ fontSize: '0.9rem', textTransform: 'capitalize', color: health.deployment?.environment === 'prod' ? '#3fb950' : '#8b949e' }}>
                        {health.deployment?.environment || 'dev'}
                      </span>
                      <span className="cs-stat-label">Environment</span>
                    </div>
                  </div>
                </div>
                {(frontendVersion?.git_commit_hash || frontendVersion?.build_timestamp) && (
                  <p style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '0.5rem' }}>
                    Frontend: {frontendVersion.git_commit_hash || ''} {frontendVersion.build_timestamp ? `· Built ${new Date(frontendVersion.build_timestamp).toLocaleString('en-GB')}` : ''}
                  </p>
                )}
              </div>

              {/* Core Services */}
              <div className="cs-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon buildings"><FaDatabase /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value" style={{ fontSize: '1rem' }}>
                      <StatusIcon ok={health.database?.status === 'connected'} /> {health.database?.response_time_ms || 0}ms
                    </span>
                    <span className="cs-stat-label">Database Response</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon today"><FaServer /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value" style={{ fontSize: '1rem' }}>{health.api?.avg_response_time_ms || 0}ms</span>
                    <span className="cs-stat-label">Avg API Response (24h)</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon total"><FaExclamationTriangle /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value" style={{ fontSize: '1rem', color: health.errors_24h > 0 ? '#f85149' : '#3fb950' }}>{health.errors_24h}</span>
                    <span className="cs-stat-label">Errors (24h)</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon properties"><FaShieldAlt /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value" style={{ fontSize: '1rem', color: health.hydrajaws_status === 'healthy' ? '#3fb950' : '#ffa657' }}>
                      {health.hydrajaws_status === 'healthy' ? 'Healthy' : health.hydrajaws_status}
                    </span>
                    <span className="cs-stat-label">Hydrajaws Sync</span>
                  </div>
                </div>
              </div>

              {/* Abuse Detection */}
              <div className="cs-section">
                <h2 className="cs-section-title"><FaExclamationCircle style={{ marginRight: '0.5rem', color: '#ffa657' }} /> Abuse Detection</h2>
                {(health.abuse_by_severity_24h && (health.abuse_by_severity_24h.WARNING > 0 || health.abuse_by_severity_24h.CRITICAL > 0)) && (
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <span style={{ color: '#3fb950' }}>INFO: {health.abuse_by_severity_24h.INFO ?? 0}</span>
                    <span style={{ color: '#ffa657' }}>WARNING: {health.abuse_by_severity_24h.WARNING ?? 0}</span>
                    <span style={{ color: '#f85149' }}>CRITICAL: {health.abuse_by_severity_24h.CRITICAL ?? 0}</span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                  {[
                    { key: 'job_creation_spam', label: 'Job creation spam (24h)' },
                    { key: 'property_creation_spam', label: 'Property creation spam (24h)' },
                    { key: 'large_batch_import', label: 'Large batch imports (24h)' },
                    { key: 'api_misuse', label: 'API misuse (24h)' },
                  ].map(({ key, label }) => (
                    <div key={key} className="cs-stat-card">
                      <div className="cs-stat-info" style={{ width: '100%' }}>
                        <span className="cs-stat-value" style={{ color: (health.abuse_24h?.[key] || 0) > 0 ? '#ffa657' : '#3fb950' }}>
                          {health.abuse_24h?.[key] ?? 0}
                        </span>
                        <span className="cs-stat-label">{label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {health.recent_abuse && health.recent_abuse.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '0.9rem', color: '#8b949e', marginBottom: '0.5rem' }}>Recent abuse events</h3>
                    <div className="cs-activity-table">
                      <table>
                        <thead><tr><th>Time</th><th>Type</th><th>Severity</th><th>User</th><th>Company</th><th>IP</th><th>Endpoint</th><th>Details</th></tr></thead>
                        <tbody>
                          {health.recent_abuse.map(ev => (
                            <tr key={ev.id}>
                              <td>{new Date(ev.created_at).toLocaleString('en-GB')}</td>
                              <td><span className="cs-badge module">{ev.event_type}</span></td>
                              <td><span style={{ color: ev.severity === 'CRITICAL' ? '#f85149' : ev.severity === 'WARNING' ? '#ffa657' : '#8b949e' }}>{ev.severity || '—'}</span></td>
                              <td>{ev.user_id ?? '—'}</td>
                              <td style={{ fontSize: '0.75rem' }}>{ev.company_id ? `${ev.company_type || ''} #${ev.company_id}` : '—'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ev.ip_address || '—'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ev.endpoint || '—'}</td>
                              <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                                {ev.details ? (typeof ev.details === 'string' ? ev.details : JSON.stringify(ev.details)).slice(0, 60) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Detailed metrics */}
              <div className="cs-section">
                <h2 className="cs-section-title">Security & Auth</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  {[
                    { label: 'Failed Logins (24h)', value: health.failed_logins_24h, warn: health.failed_logins_24h > 10 },
                    { label: 'Webhook Failures (24h)', value: health.webhook_failures_24h, warn: health.webhook_failures_24h > 0 },
                    { label: 'Error Rate (24h)', value: health.errors_24h, warn: health.errors_24h > 5 },
                  ].map((m, i) => (
                    <div key={i} className="cs-stat-card">
                      <div className="cs-stat-info" style={{ width: '100%' }}>
                        <span className="cs-stat-value" style={{ color: m.warn ? '#f85149' : '#3fb950' }}>{m.value}</span>
                        <span className="cs-stat-label">{m.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Errors */}
              {health.recent_errors && health.recent_errors.length > 0 && (
                <div className="cs-section">
                  <h2 className="cs-section-title">Recent Errors</h2>
                  <div className="cs-activity-table">
                    <table>
                      <thead><tr><th>Time</th><th>Type</th><th>Endpoint</th><th>Message</th><th>Code</th></tr></thead>
                      <tbody>
                        {health.recent_errors.map(err => (
                          <tr key={err.id}>
                            <td>{new Date(err.created_at).toLocaleString('en-GB')}</td>
                            <td><span className="cs-badge module">{err.error_type}</span></td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{err.endpoint || '—'}</td>
                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{err.error_message}</td>
                            <td>{err.response_code || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {lastRefresh && (
                <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#484f58', marginTop: '2rem' }}>
                  Last refreshed: {lastRefresh.toLocaleTimeString('en-GB')}
                </p>
              )}
            </>
          ) : (
            <div className="cs-empty-state"><FaTimesCircle /><p>Could not load health data</p></div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SystemHealth;
